import { ArrowRight, Check } from "lucide-react";
import { Button } from "../ui/button";
import SectionLogo from "../SectionLogo";

const EVENTS = [
  "5K & 10K Road Races",
  "Half Marathons & Marathons",
  "Track & Field Meets",
  "Cross Country Events",
  "Relay Races & Obstacle Courses",
  "Trail Running Events",
];

const TREATMENT_AREAS = [
  { area: "Quads & Hamstrings", desc: "Reduce soreness and tightness after sprints or long distance" },
  { area: "Knees & IT Band", desc: "Target joint inflammation and runner's knee pain" },
  { area: "Calves & Achilles", desc: "Relieve strain from repetitive impact and prevent injury" },
  { area: "Hips & Lower Back", desc: "Release tension from running posture and stride impact" },
  { area: "Shins & Ankles", desc: "Combat shin splints and ankle inflammation" },
];

export const RacesTrackSection = ({ scrollToSection }) => {
  return (
    <section id="races-track" className="py-24 bg-[#0a0318] relative">
      <SectionLogo position="right" size="large" opacity={0.05} />
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-amber-500/10 border border-amber-500/30 rounded-full px-6 py-2 mb-6">
              <span className="text-amber-300 font-medium">Races & Track Events</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-amber-400">Race Day</span> <span className="text-white">Recovery</span>
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Whether you&apos;re running a 5K, 10K, or competing in track & field events, Ice Man Cryo helps you
              recover faster and perform at your peak.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              <div className="bg-[#110422]/50 border border-amber-500/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-amber-400 mb-4">For Runners & Track Athletes</h3>
                <p className="text-white/70 mb-4">
                  Running puts intense strain on your legs, hips, and joints. Cryotherapy targets the exact areas that
                  take the biggest hit — quads, hamstrings, calves, IT bands, and knees. Whether you just crossed the
                  finish line or you&apos;re between events at a track meet, the SubZero device delivers instant relief
                  and reduces inflammation so you can recover faster and get back to competing.
                </p>
                <h4 className="text-lg font-bold text-white mb-3">How We Help at Race Events</h4>
                <p className="text-white/70 mb-4">
                  Ice Man Cryo sets up right at the finish line or in the athlete recovery area. We treat runners
                  immediately after they compete — when the muscles are most inflamed and recovery matters most. Our
                  mobile setup means we come directly to the event, no appointment needed.
                </p>
              </div>

              <h3 className="text-xl font-bold text-white">Events We Cover</h3>
              <div className="space-y-3">
                {EVENTS.map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 bg-[#110422]/50 border border-amber-500/20 rounded-lg p-4"
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-600 to-amber-400 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-[#110422]/50 border border-amber-500/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Treatment Areas for Runners</h3>
                <div className="space-y-3">
                  {TREATMENT_AREAS.map((item) => (
                    <div key={item.area} className="border-b border-amber-500/10 pb-3 last:border-0">
                      <span className="text-amber-400 font-semibold">{item.area}:</span>
                      <span className="text-white/70 ml-2">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#110422]/50 border border-amber-500/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Pricing</h3>
                <div className="space-y-2">
                  <h4 className="text-amber-400 font-bold text-sm uppercase tracking-wider">Team Packages</h4>
                  <div className="flex justify-between items-center py-3 border-b border-amber-500/20">
                    <span className="text-gray-300">Single Event</span>
                    <span className="text-amber-400 font-bold text-lg">$400–$600</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-amber-500/20">
                    <span className="text-gray-300">3-Event Package</span>
                    <span className="text-amber-400 font-bold text-lg">$1,050</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-amber-500/20">
                    <span className="text-gray-300">Season Package</span>
                    <span className="text-amber-400 font-bold text-lg">Contact Us</span>
                  </div>
                </div>
                <div className="space-y-2 mt-6">
                  <h4 className="text-amber-400 font-bold text-sm uppercase tracking-wider">Individual Packages</h4>
                  <div className="flex justify-between items-center py-3 border-b border-amber-500/20">
                    <span className="text-gray-300">Single Session</span>
                    <span className="text-amber-400 font-bold text-lg">$40–$80</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-amber-500/20">
                    <span className="text-gray-300">3-Session Package</span>
                    <span className="text-amber-400 font-bold text-lg">$75</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-amber-500/20">
                    <span className="text-gray-300">Monthly Unlimited</span>
                    <span className="text-amber-400 font-bold text-lg">Contact Us</span>
                  </div>
                </div>
                <div className="py-3 mt-4">
                  <p className="text-white/70 text-sm mb-2">
                    Price depends on the number of athletes and event duration.
                  </p>
                  <p className="text-amber-400 font-semibold text-sm">
                    All packages must be prepaid. 20% discount on bulk bookings.
                  </p>
                </div>
                <div className="mt-6">
                  <Button
                    onClick={() => scrollToSection("booking")}
                    className="w-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600 text-white py-6"
                    data-testid="races-track-book-btn"
                  >
                    Book Race Day Recovery <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
